/* Field kit — small controlled inputs that render the mockup `.field` markup
   (label + .req/.opt + .input + .help + .vmsg) used across the wizard steps. */
import React from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {cx, SearchSelect} from '../../components'

/* Scroll the first invalid field into view and focus it, so a failed "Continue"
   doesn't leave the user hunting up/down the page for the missing field. Runs on
   a short delay so the invalid classes are painted first. Safe to call
   unconditionally — it no-ops when nothing on the page is marked invalid. */
export function scrollToFirstError() {
  if (typeof document === 'undefined') return
  setTimeout(() => {
    const el = document.querySelector('.field.invalid, .mdsec.invalid, .p3sec.invalid, .input.err, .xfrow .err')
    if (!el) return
    el.scrollIntoView({behavior: 'smooth', block: 'center'})
    const focusable = el.matches('input, textarea, select') ? el : el.querySelector('input, textarea, select')
    if (focusable && focusable.focus) focusable.focus({preventScroll: true})
  }, 40)
}

export function Field({label, value, onChange, type, options, ph, help, opt, invalid, msg, readOnly}) {
  const input =
    type === 'textarea' ? (
      <textarea
        className={cx('input', invalid && 'err')}
        rows={2}
        placeholder={ph || ''}
        value={value || ''}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : type === 'select' ? (
      <SearchSelect value={value || ''} onChange={(v) => onChange(v)} options={options || []} invalid={invalid} placeholder='Select…' disabled={readOnly} />
    ) : (
      <input
        className={cx('input', invalid && 'err')}
        type={type || 'text'}
        /* number inputs default to step=1, which flags decimals like 0.5 kg as invalid in the browser */
        step={type === 'number' ? 'any' : undefined}
        placeholder={ph || ''}
        value={value || ''}
        readOnly={readOnly}
        style={readOnly ? {background: '#f1f3f5', color: '#9aa4b2', borderColor: '#e5e7eb', cursor: 'not-allowed'} : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    )
  return (
    <div className={cx('field', invalid && 'invalid')}>
      <label>
        {label} {opt ? <span className='opt'>(optional)</span> : readOnly ? null : <span className='req'>*</span>}
      </label>
      {input}
      {help ? (
        <div className='help' style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          {help}
        </div>
      ) : null}
      <div className='vmsg'>{msg || 'Required.'}</div>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.node,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  options: PropTypes.array,
  ph: PropTypes.string,
  help: PropTypes.node,
  opt: PropTypes.bool,
  invalid: PropTypes.bool,
  msg: PropTypes.string,
  readOnly: PropTypes.bool,
}

/* Footer — Back / (Skip) / Continue|Save & Complete cluster. */
export function Footer({step, last, skip, disabled, nextLabel, onBack, onSkip, onNext}) {
  return (
    <div className='row gap12' style={{marginTop: 16, justifyContent: 'flex-end', display: 'flex', gap: 12}}>
      {skip ? (
        <button type='button' className='btn' style={{marginRight: 'auto'}} onClick={onSkip}>
          Skip for now →
        </button>
      ) : null}
      {step > 0 ? (
        <button type='button' className='btn' onClick={onBack}>
          ← Back
        </button>
      ) : null}
      <button type='button' className='btn btn-primary' disabled={disabled} onClick={onNext}>
        {last ? 'Save & Complete' : nextLabel || 'Continue'} →
      </button>
    </div>
  )
}

Footer.propTypes = {
  step: PropTypes.number,
  last: PropTypes.bool,
  skip: PropTypes.bool,
  disabled: PropTypes.bool,
  nextLabel: PropTypes.string,
  onBack: PropTypes.func,
  onSkip: PropTypes.func,
  onNext: PropTypes.func,
}
